"use client"

import { useState, useEffect } from "react"
import { useSearchParams } from "next/navigation"
import { createClient } from "@/lib/supabase/client"
import { toast } from "sonner"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { Mail } from "lucide-react"

export function SignupForm({
  className,
  ...props
}: React.ComponentProps<"div">) {
  const searchParams = useSearchParams()
  const [email, setEmail] = useState("")
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [otp, setOtp] = useState("")
  const [loading, setLoading] = useState(false)
  const [otpSent, setOtpSent] = useState(false)

  useEffect(() => {
    const emailParam = searchParams.get("email")
    if (emailParam) {
      setEmail(decodeURIComponent(emailParam))
      toast.info("Completa la registrazione con nome e cognome")
    }
  }, [searchParams])

  const handleSignUp = async () => {
    if (!email || !firstName || !lastName) { toast.error("Compila tutti i campi"); return }
    setLoading(true)
    try {
      const verifyResponse = await fetch("/api/auth/verify-signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      })
      const verifyData = await verifyResponse.json()
      if (!verifyResponse.ok) throw new Error(verifyData.error || "🚫 Questa email non è autorizzata alla beta")

      sessionStorage.setItem("pending_user_data", JSON.stringify({ firstName, lastName, email }))

      const supabase = createClient()
      const { error: otpError } = await supabase.auth.signInWithOtp({ email, options: { shouldCreateUser: true } })
      if (otpError) throw new Error("Errore durante l'invio del codice: " + otpError.message)

      toast.success("Codice inviato! Controlla la tua email.")
      setOtpSent(true)
    } catch (error: unknown) {
      const err = error as { message?: string }
      toast.error(err.message || "Errore durante la registrazione")
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 8) { toast.error("Inserisci il codice a 8 cifre"); return }
    setLoading(true)
    try {
      const supabase = createClient()
      const { data, error } = await supabase.auth.verifyOtp({ email, token: otp, type: "email" })
      if (error) throw error

      const pendingData = JSON.parse(sessionStorage.getItem("pending_user_data") || "null")
      if (data.user) {
        await supabase.from("users").upsert({
          id: data.user.id,
          email: data.user.email,
          name: pendingData ? `${pendingData.firstName} ${pendingData.lastName}`.trim() : data.user.email?.split("@")[0],
          first_name: pendingData?.firstName || null,
          last_name: pendingData?.lastName || null,
          email_verified: new Date().toISOString(),
        }, { onConflict: "id" })
        sessionStorage.removeItem("pending_user_data")
      }

      toast.success("Registrazione completata con successo!")
      await new Promise((r) => setTimeout(r, 500))
      window.location.href = "/dashboard"
    } catch (error: unknown) {
      const err = error as { message?: string }
      toast.error(err.message || "Codice non valido o scaduto")
    } finally {
      setLoading(false)
    }
  }

  if (otpSent) {
    return (
      <div className={cn("flex flex-col gap-6", className)} {...props}>
        <Card>
          <CardHeader className="text-center">
            <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-orange-100">
              <Mail className="h-6 w-6 text-orange-600" />
            </div>
            <CardTitle className="text-xl">Inserisci il codice</CardTitle>
            <CardDescription>
              Ti abbiamo inviato un codice a 8 cifre a{" "}
              <span className="font-semibold text-foreground">{email}</span>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <FieldGroup>
              <Field>
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={8}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                  onKeyDown={(e) => e.key === "Enter" && handleVerifyOtp()}
                  placeholder="00000000"
                  className="text-center text-2xl font-bold tracking-widest h-14"
                  autoFocus
                />
              </Field>
              <Field>
                <Button
                  type="button"
                  className="w-full"
                  disabled={loading || otp.length !== 8}
                  onClick={handleVerifyOtp}
                >
                  {loading ? "Verifica in corso..." : "Completa Registrazione"}
                </Button>
                <FieldDescription className="flex justify-between">
                  <button
                    type="button"
                    className="text-primary underline-offset-4 hover:underline text-sm"
                    onClick={() => { setOtpSent(false); setOtp("") }}
                  >
                    ← Cambia dati
                  </button>
                  <button
                    type="button"
                    className="underline-offset-4 hover:underline text-sm"
                    disabled={loading}
                    onClick={handleSignUp}
                  >
                    Invia nuovo codice
                  </button>
                </FieldDescription>
              </Field>
            </FieldGroup>
          </CardContent>
        </Card>
        <FieldDescription className="px-6 text-center">
          Non hai ricevuto il codice? Controlla la cartella spam.
        </FieldDescription>
      </div>
    )
  }

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <Card>
        <CardHeader className="text-center">
          <CardTitle className="text-xl">Crea il tuo account</CardTitle>
          <CardDescription>
            Inserisci i tuoi dati per registrarti
          </CardDescription>
        </CardHeader>
        <CardContent>
          <FieldGroup>
            <div className="grid grid-cols-2 gap-4">
              <Field>
                <FieldLabel htmlFor="firstName">Nome</FieldLabel>
                <Input
                  id="firstName"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Mario"
                  required
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="lastName">Cognome</FieldLabel>
                <Input
                  id="lastName"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Rossi"
                  required
                />
              </Field>
            </div>
            <Field>
              <FieldLabel htmlFor="email">Email</FieldLabel>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="mario.rossi@email.com"
                required
              />
            </Field>
            <Field>
              <Button
                type="button"
                className="w-full"
                disabled={loading || !email || !firstName || !lastName}
                onClick={handleSignUp}
              >
                {loading ? "Invio in corso..." : "Registrati"}
              </Button>
              <FieldDescription className="text-center">
                Hai già un account?{" "}
                <a href="/auth/signin" className="underline underline-offset-4">
                  Accedi
                </a>
              </FieldDescription>
            </Field>
          </FieldGroup>
        </CardContent>
      </Card>
      <FieldDescription className="px-6 text-center">
        Registrandoti accetti i nostri{" "}
        <a href="/terms" className="underline underline-offset-4">Termini di Servizio</a>{" "}
        e{" "}
        <a href="/privacy" className="underline underline-offset-4">Privacy Policy</a>.
      </FieldDescription>
    </div>
  )
}
